import "react-range-slider-input/dist/style.css"

import { Cup, Gift, MagicStar, TickCircle } from "iconsax-reactjs"
import React, { useCallback, useEffect, useRef } from "react"
import { Controller, useForm } from "react-hook-form"
import RangeSlider from "react-range-slider-input"

import { BottomSheetModal, IconButton } from "@/components/shared"
import { ToggleFilter } from "@/components/shared/ui"
import { Button, Checkbox } from "@/components/ui"
import { EFilterAffiliate } from "@/modules/helpers/enum"
import { useAffiliateCategories } from "@/modules/helpers/hooks"
import { transformFilterToQueryParams, useFilterStore } from "@/modules/helpers/hooks/shared"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import type { AffiliateCategory } from "@/modules/types"

// Types
interface FilterProps {
  refetch?: () => void
  onChange: (value: string) => void
  onChangeCategory: (value: string | null) => void
  category: string
  value: string
}

interface CategoryItemProps {
  active: boolean
  label: string
  index: number
  onClick: (x: number) => void
  data?: AffiliateCategory
  type?: "default" | "button"
}

// Utility functions
const calculateScrollPosition = (data: AffiliateCategory | undefined, index: number): number => {
  if (!data) return 0

  const textWidth = data.slice(0, index).reduce((total, curr) => total + curr.length * 10, 0)
  const spacingWidth = (data.slice(0, index)?.length || 0) * 20

  return textWidth + spacingWidth
}

// Sub-components
const CategoryItem: React.FC<CategoryItemProps> = ({
  active,
  label,
  data,
  type = "default",
  onClick,
  index
}) => (
  <IconButton
    onClick={() => {
      const scrollPosition = calculateScrollPosition(data, index)
      onClick(scrollPosition)
    }}
    className={cn(
      "flex items-center gap-1 bg-gradient-primary",
      type === "button"
        ? cn(
            "p-3 w-auto h-8 border",
            active ? "border-primary bg-gradient-primary" : "border-gray-300 bg-gradient-primary"
          )
        : cn(
            "px-4 w-auto border-b-2 h-7 rounded-none",
            active ? "border-primary bg-gradient-primary" : "border-gray-50"
          )
    )}
    mode="ghost"
  >
    <div className="flex flex-row items-center gap-1">
      <p className="text-black font-semibold whitespace-nowrap text-xs capitalize">{label}</p>
    </div>
  </IconButton>
)

const CategoryList: React.FC<{
  categories: AffiliateCategory | undefined
  activeCategory: string
  onCategoryChange: (category: string | null, scrollPosition: number) => void
  scrollContainerRef: React.RefObject<HTMLDivElement | null>
  type?: "default" | "button"
}> = ({ categories, activeCategory, onCategoryChange, scrollContainerRef, type = "default" }) => {
  return (
    <div
      ref={type === "default" ? scrollContainerRef : undefined}
      className={cn(
        "flex",
        type === "default" ? "w-full px-1 pb-2 overflow-scroll show-scroll" : "w-full flex-wrap"
      )}
    >
      <CategoryItem
        active={!activeCategory}
        onClick={(x: number) => onCategoryChange(null, x)}
        type={type}
        data={categories}
        index={0}
        label="Tất cả"
      />
      {categories?.map((category, index) => (
        <CategoryItem
          key={category}
          index={index}
          data={categories}
          active={activeCategory === category}
          onClick={(x: number) => onCategoryChange(category, x)}
          type={type}
          label={category}
        />
      ))}
    </div>
  )
}

const FilterList: React.FC<{
  activeValue: string
  onFilterChange: (value: string) => void
}> = ({ activeValue, onFilterChange }) => {
  return (
    <div className="flex justify-between w-full">
      <ToggleFilter
        options={[
          {
            value: EFilterAffiliate.SUITABLE,
            label: "Dành cho bạn",
            icon: <Gift />
          },
          {
            value: EFilterAffiliate.EASY_SELL,
            label: "Sản phẩm dễ bán",
            icon: <Cup />
          },
          {
            value: EFilterAffiliate.HOT,
            label: "Hoa hồng cao",
            icon: <MagicStar />
          }
        ]}
        value={activeValue}
        onChange={onFilterChange}
        className="flex-1"
      />
    </div>
  )
}

// Main Component
const FilterTabs: React.FC<FilterProps> = ({ onChange, onChangeCategory, category, value }) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  // Zustand store
  const { showAllCategory } = useFilterStore()

  const { data: affiliateCategories } = useAffiliateCategories()

  const handleCategorySelectAndScroll = useCallback(
    (category: string | null, scrollPosition: number) => {
      onChangeCategory(category)

      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          left: scrollPosition,
          behavior: "smooth"
        })
      }
    },
    [onChangeCategory]
  )

  return (
    <div className="bg-white w-full px-2 mt-2">
      {showAllCategory && (
        <div className="flex justify-between gap-2">
          <CategoryList
            categories={affiliateCategories?.data?.data}
            activeCategory={category}
            onCategoryChange={handleCategorySelectAndScroll}
            scrollContainerRef={scrollContainerRef}
            type="button"
          />
        </div>
      )}
      {/* {!!affiliateCategories?.data?.data?.length && (
        <div className="flex justify-between">
          <CategoryList
            categories={affiliateCategories?.data?.data}
            activeCategory={category}
            onCategoryChange={handleCategorySelectAndScroll}
            scrollContainerRef={scrollContainerRef}
            type="default"
          />
        </div>
      )} */}
      <div className="flex justify-between gap-2">
        <FilterList activeValue={value} onFilterChange={onChange} />
      </div>
    </div>
  )
}

interface ModalFilterProps {
  refetch?: () => void
  isVisible: boolean
  setIsVisible: (isVisible: boolean) => void
}

const defaultValues = {
  est: { select: "all", rang: { startPrice: 1000, endPrice: 500000 } },
  commission: "",
  sortBy: "NEWEST"
}

export const Filter: React.FC<ModalFilterProps> = ({ refetch, isVisible, setIsVisible }) => {
  const { filterState, reset, setFilter } = useFilterStore()
  const form = useForm({
    defaultValues
  })

  // Load current filter values into form when modal opens
  useEffect(() => {
    if (isVisible && filterState) {
      const formValues = {
        sortBy:
          filterState.sortBy === "createdAt"
            ? filterState.order === "ASC"
              ? "OLDEST"
              : "NEWEST"
            : "NEWEST",
        commission: filterState.commission || "",
        est: filterState.est || defaultValues.est
      }
      form.reset(formValues)
    }
  }, [isVisible, filterState, form])

  const resetFilterForm = async () => {
    reset()
    form.reset(defaultValues)
    refetch?.()
  }

  const applyFilterAndClose = useCallback(
    async (data: any) => {
      // Transform form data to API query params
      const queryParams = transformFilterToQueryParams(data)
      // Store both the raw form data and the transformed query params
      setFilter({ ...data, ...queryParams })
      refetch?.()
      setIsVisible(false)
    },
    [refetch, setFilter, setIsVisible]
  )

  // Use countFilter from store instead of calculating locally
  // The store automatically calculates the count when filter changes

  return (
    <BottomSheetModal isVisible={isVisible} setIsVisible={setIsVisible} title="Bộ lọc">
      <div className="flex flex-col gap-6 p-2 px-4">
        {/* Giá cả */}
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-700">Sắp xếp theo</label>
            <Controller
              name="sortBy"
              control={form.control}
              render={({ field }) => (
                <div className="flex gap-2">
                  {[
                    { key: "NEWEST", label: "Mới nhất" },
                    { key: "OLDEST", label: "Cũ nhất" }
                  ].map((item) => {
                    const isSelected = field.value === item.key
                    return (
                      <Button
                        key={item.key}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "text-xs rounded-lg flex items-center gap-2 px-3 py-2.5 h-auto",
                          {
                            "bg-white border-gray-300 text-gray-800": isSelected,
                            "bg-white border-gray-300": !isSelected
                          }
                        )}
                        onClick={() => field.onChange(item.key)}
                      >
                        {isSelected ? (
                          <div className="w-4 h-4 rounded-full flex items-center justify-center">
                            <TickCircle size={12} className="text-secondary" variant="Bold" />
                          </div>
                        ) : (
                          <div className="w-4 h-4 rounded-lg border border-gray-300" />
                        )}
                        {item.label}
                      </Button>
                    )
                  })}
                </div>
              )}
            />
          </div>
          <p className="text-black font-normal text-sm mt-2">Giá cả</p>
          <Controller
            name="est"
            control={form.control}
            render={({ field: { value: estSelect, onChange } }) => {
              const minValue = estSelect.rang?.startPrice || 1000
              const maxValue = estSelect.rang?.endPrice || 500000
              const minPercent = ((minValue - 1000) / (500000 - 1000)) * 100
              const maxPercent = ((maxValue - 1000) / (500000 - 1000)) * 100

              return (
                <div className="flex flex-col gap-4 mt-2">
                  <div className="space-y-2 relative pt-8">
                    {/* Tooltips trên các thumb */}
                    <div
                      className="absolute -top-1 bg-primary text-white px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap z-10"
                      style={{
                        left: `calc(${minPercent}% - 0px)`,
                        transform: "translateX(-10%)"
                      }}
                    >
                      {minValue.toLocaleString()}đ
                    </div>
                    <div
                      className="absolute -top-1 bg-primary text-white px-2 py-1 rounded text-[10px] font-medium whitespace-nowrap z-10"
                      style={{
                        left: `calc(${maxPercent}%)`,
                        transform: "translateX(-100%)"
                      }}
                    >
                      {maxValue.toLocaleString()}đ
                    </div>

                    <RangeSlider
                      defaultValue={[1000, 500000]}
                      min={1000}
                      max={500000}
                      step={1000}
                      ariaLabel={["Giá thấp", "Giá cao"]}
                      value={[minValue, maxValue]}
                      className={`${estSelect.select === "rang" ? "text-primary" : "opacity-50"}`}
                      onInput={([min, max]) => {
                        onChange({ select: "rang", rang: { startPrice: min, endPrice: max } })
                      }}
                    />
                  </div>
                  <div className="flex justify-between gap-4 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={estSelect.select === "all"}
                        onCheckedChange={(checked) =>
                          checked &&
                          onChange({
                            select: "all",
                            rang: { startPrice: 1000, endPrice: 500000 }
                          })
                        }
                      />
                      <span className="text-xs">Toàn bộ mức giá</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={estSelect.select === "over"}
                        onCheckedChange={(checked) =>
                          checked &&
                          onChange({
                            select: "over",
                            rang: { startPrice: 500000, endPrice: 999999999 }
                          })
                        }
                      />
                      <span className="text-xs">Hơn 500K</span>
                    </label>
                  </div>
                </div>
              )
            }}
          />
        </div>

        {/* Tỷ lệ hoa hồng */}
        <div className="flex flex-col gap-3">
          <p className="text-black font-normal text-sm mt-2">Tỷ lệ hoa hồng</p>
          <Controller
            name="commission"
            control={form.control}
            render={({ field }) => (
              <div className="flex flex-wrap gap-2">
                {[
                  { key: "OVER_15", label: "Nhiều hơn 15%" },
                  { key: "B10_15", label: "10%-15%" },
                  { key: "B5_10", label: "5%-10%" },
                  { key: "B3_5", label: "3%-5%" }
                ].map((item) => (
                  <Button
                    key={item.key}
                    variant={"outline"}
                    size="sm"
                    className={cn("text-xs rounded-lg px-4 py-2.5 h-10", {
                      "bg-gradient-primary border-0 text-white": field.value === item.key,
                      "bg-white border-gray-300": field.value !== item.key
                    })}
                    onClick={() => {
                      // toggle
                      if (field.value === item.key) {
                        field.onChange(null)
                      } else {
                        field.onChange(item.key)
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
            )}
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-4 mt-4">
          <Button
            variant="ghost"
            className="w-28 text-xs h-12 border border-gray-400 text-gray-600 rounded-xl"
            onClick={resetFilterForm}
          >
            Đặt lại
          </Button>
          <Button
            className="flex-1 text-xs h-12 rounded-xl"
            onClick={() => {
              form.handleSubmit(applyFilterAndClose)()
            }}
          >
            Xem kết quả
          </Button>
        </div>
      </div>
    </BottomSheetModal>
  )
}

export { FilterTabs }
